import { z } from 'zod';
import { persistedTimestampSchema, persistedTimestampToDate } from '../shared/firestore-timestamp.schema.js';
import { asDomainRecord } from '../shared/domain-value.js';
import { currencyCodeSchema, processStepStatusSchema, } from '../shared/shipment-primitives.schema.js';
/** Nombre contractuel d’étapes du traitement backoffice d’un colis lourd. */
export const HEAVY_WORKFLOW_STEP_COUNT = 14;
export const HEAVY_WORKFLOW_STEP_CATALOG = [
    { stepNumber: 1, name: 'Demande reçue', description: 'Colis lourd enregistré' },
    {
        stepNumber: 2,
        name: 'Analyser la demande',
        description: 'Vérifier poids, dimensions, contenu',
    },
    { stepNumber: 3, name: 'Calculer estimation', description: 'Saisir prix estimé' },
    {
        stepNumber: 4,
        name: 'Contacter société(s) tierce(s)',
        description: 'Recherche partenaires transport',
    },
    { stepNumber: 5, name: 'Société contactée', description: 'Date + nom société' },
    { stepNumber: 6, name: 'Devis reçu société', description: 'Upload PDF devis tiers' },
    { stepNumber: 7, name: 'Prix final validé', description: 'Confirmer prix définitif' },
    { stepNumber: 8, name: 'Générer devis PDF', description: 'Créer devis client' },
    {
        stepNumber: 9,
        name: 'Envoyer devis client',
        description: 'Email automatique avec liens accepter/refuser',
    },
    { stepNumber: 10, name: 'Attente réponse', description: 'Timer 7 jours par défaut' },
    { stepNumber: 11, name: 'Client a accepté', description: 'Génération lien paiement' },
    { stepNumber: 12, name: 'Envoyer lien paiement', description: 'Email Stripe Payment Link' },
    { stepNumber: 13, name: 'Paiement reçu', description: 'Confirmation paiement Stripe' },
    { stepNumber: 14, name: 'Prêt pour collecte', description: 'Configuration collecte spécialisée' },
];
/** États autorisés pour une étape du workflow lourd. */
export const heavyWorkflowStepStatusSchema = processStepStatusSchema.extract([
    'pending',
    'in_progress',
    'completed',
    'skipped',
]);
/**
 * Une étape persistée conserve son libellé et ses jalons afin qu’une
 * modification future du catalogue ne réécrive pas l’historique en cours.
 */
export const heavyWorkflowStepSchema = z
    .object({
    stepNumber: z.number().int().min(1).max(HEAVY_WORKFLOW_STEP_COUNT),
    name: z.string().min(1),
    description: z.string(),
    status: heavyWorkflowStepStatusSchema,
    startedAt: persistedTimestampSchema.optional(),
    completedAt: persistedTimestampSchema.optional(),
    completedBy: z.string().optional(),
    notes: z.string().optional(),
})
    .strict();
/**
 * Map Firestore indexée de `"1"` à `"14"`. Une map permet les mises à jour
 * pointées d’une étape sans remplacer ou tronquer les autres étapes.
 */
export const heavyWorkflowStepMapSchema = z
    .record(heavyWorkflowStepSchema)
    .superRefine((steps, context) => {
    if (Object.keys(steps).length !== HEAVY_WORKFLOW_STEP_COUNT) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Le workflow doit contenir exactement 14 étapes',
        });
    }
    for (let step = 1; step <= HEAVY_WORKFLOW_STEP_COUNT; step++) {
        if (!steps[String(step)] || steps[String(step)].stepNumber !== step) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: [String(step)],
                message: `Étape ${step} requise`,
            });
        }
    }
});
/** Prestataire sollicité et, lorsqu’il a répondu, données de son devis. */
export const thirdPartyCompanySchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    contactPerson: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    quotePdfUrl: z.string().optional(),
    quoteAmount: z.number().finite().nonnegative().optional(),
    quoteCurrency: currencyCodeSchema,
    quoteReceivedDate: persistedTimestampSchema.optional(),
    agreedDate: persistedTimestampSchema.optional(),
    notes: z.string().optional(),
})
    .strict();
/** Campagne de consultation des transporteurs et prestataire finalement retenu. */
export const thirdPartyQuoteSchema = z
    .object({
    contacted: z.boolean(),
    contactDate: persistedTimestampSchema.optional(),
    companies: z.array(thirdPartyCompanySchema),
    selectedCompanyId: z.string().optional(),
})
    .strict();
/**
 * Devis BeFret présenté au client. Il est distinct des devis transporteurs :
 * son montant inclut la composition finale, ses conditions et la réponse du client.
 */
export const heavyClientQuoteSchema = z
    .object({
    quoteNumber: z.string().optional(),
    quoteAmount: z.number().finite().nonnegative(),
    quoteCurrency: currencyCodeSchema,
    quotePdfUrl: z.string().optional(),
    quoteGeneratedAt: persistedTimestampSchema.optional(),
    quoteSentAt: persistedTimestampSchema.optional(),
    validUntil: persistedTimestampSchema.optional(),
    acceptanceToken: z.string().optional(),
    rejectionToken: z.string().optional(),
    clientAccepted: z.boolean().optional(),
    clientResponseDate: persistedTimestampSchema.optional(),
    expiredAtResponse: z.boolean().optional(),
    clientRejectionReason: z.string().optional(),
    clientNotes: z.string().optional(),
    priceBreakdown: z
        .object({
        transportCost: z.number(),
        handlingCost: z.number(),
        insuranceCost: z.number().optional(),
        customsFees: z.number().optional(),
        otherFees: z.number().optional(),
    })
        .strict()
        .nullable()
        .optional(),
    specialConditions: z.string().nullable().optional(),
    estimatedDelivery: z.string().nullable().optional(),
    validityDays: z.number().int().positive().optional(),
})
    .strict();
/**
 * Paiement Stripe propre au devis lourd. L’anomalie conserve le montant reçu
 * lorsqu’un webhook arrive après refus, annulation ou avec un sous-paiement.
 */
export const HEAVY_PAYMENT_ANOMALY_REASONS = [
    'quote_rejected',
    'shipment_cancelled',
    'amount_underpaid',
];
export const heavyPaymentAnomalyReasonSchema = z.enum(HEAVY_PAYMENT_ANOMALY_REASONS);
export const heavyPaymentSchema = z
    .object({
    stripePaymentLinkId: z.string().optional(),
    paymentLinkUrl: z.string().optional(),
    sentAt: persistedTimestampSchema.optional(),
    paidAt: persistedTimestampSchema.optional(),
    amount: z.number().finite().nonnegative().optional(),
    chargedAmountCents: z.number().int().nonnegative().optional(),
    currency: currencyCodeSchema.optional(),
    paymentIntentId: z.string().optional(),
    singleUse: z.boolean().optional(),
    quotedAmount: z.number().finite().nonnegative().nullable().optional(),
    amountDeltaCents: z.number().int().optional(),
    anomaly: z
        .object({
        reason: heavyPaymentAnomalyReasonSchema,
        sessionId: z.string(),
        paymentIntentId: z.string().nullable(),
        amountTotalCents: z.number().int().nullable(),
        amountDeltaCents: z.number().int(),
        detectedAt: persistedTimestampSchema,
    })
        .strict()
        .optional(),
})
    .strict();
/** Configuration validée à l’étape 14 avant la collecte spécialisée. */
export const heavyCollectionConfigSchema = z
    .object({
    labelDeliveryMode: z.enum(['email', 'driver']),
    selectedPartnerId: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    scheduledDate: z.string().optional(),
    confirmedAt: persistedTimestampSchema.optional(),
})
    .strict();
/** Agrégat du devis, de la décision client, du paiement et de la collecte. */
export const heavyWorkflowSchema = z
    .object({
    currentStep: z.number().int().min(1).max(HEAVY_WORKFLOW_STEP_COUNT),
    status: z.enum(['pending', 'in_progress', 'completed', 'rejected', 'cancelled']),
    startedAt: persistedTimestampSchema.optional(),
    completedAt: persistedTimestampSchema.optional(),
    steps: heavyWorkflowStepMapSchema,
    thirdPartyQuote: thirdPartyQuoteSchema.optional(),
    clientQuote: heavyClientQuoteSchema.optional(),
    payment: heavyPaymentSchema.optional(),
    collectionConfig: heavyCollectionConfigSchema.optional(),
    estimatedPrice: z.number().finite().nonnegative().optional(),
})
    .strict();
/** Note privée du backoffice, avec priorité et suivi de résolution. */
export const heavyInternalNoteSchema = z
    .object({
    id: z.string(),
    agent: z.string(),
    note: z.string(),
    createdAt: persistedTimestampSchema,
    priority: z.enum(['low', 'medium', 'high', 'urgent']),
    category: z.enum(['assessment', 'logistics', 'customer', 'technical', 'admin']),
    resolved: z.boolean(),
    resolvedAt: persistedTimestampSchema.optional(),
})
    .strict();
/** Communication entrante ou sortante conservée dans le dossier lourd. */
export const heavyCommunicationSchema = z
    .object({
    id: z.string(),
    type: z.enum(['email', 'phone', 'whatsapp', 'sms', 'meeting', 'system']),
    direction: z.enum(['inbound', 'outbound']),
    subject: z.string().optional(),
    content: z.string(),
    from: z.string(),
    to: z.string(),
    timestamp: persistedTimestampSchema,
    attachments: z.array(z.string()),
    status: z.enum(['sent', 'delivered', 'read', 'responded', 'failed']),
    followUpRequired: z.boolean(),
    followUpDate: persistedTimestampSchema.optional(),
})
    .strict();
/** Choix de traitement physique : transport sur mesure ou division en plusieurs colis. */
export const heavyProcessingInfoSchema = z
    .object({
    processingType: z.enum(['custom_shipping', 'split_shipment']),
    customShipping: z
        .object({
        specialRequirements: z.array(z.string()),
        transportMethod: z.string(),
        estimatedDuration: z.string(),
    })
        .strict()
        .optional(),
    splitShipment: z
        .object({
        originalWeight: z.number().positive(),
        packageCount: z.number().int().positive(),
        weightPerPackage: z.number().positive(),
        splitReason: z.string(),
    })
        .strict()
        .optional(),
    processingStarted: persistedTimestampSchema.optional(),
    processingCompleted: persistedTimestampSchema.optional(),
    collectionDate: z.string().optional(),
})
    .strict();
/** Documents et preuves photographiques produits pendant le traitement lourd. */
export const heavyDocumentsSchema = z
    .object({
    initialRequest: z.string().optional(),
    quotationPdf: z.string().optional(),
    acceptanceDocument: z.string().optional(),
    assessmentPhotos: z.array(z.string()),
    packagingPhotos: z.array(z.string()),
    deliveryPhotos: z.array(z.string()),
    customerInvoice: z.string().optional(),
    deliveryReceipt: z.string().optional(),
})
    .strict();
/** Données exclusivement opérationnelles, non fournies par le client à la création. */
export const heavyBackofficeSchema = z
    .object({
    assignedAgent: z.string().optional(),
    assignedAt: persistedTimestampSchema.optional(),
    internalNotes: z.array(heavyInternalNoteSchema),
    specialRequirements: z.array(z.string()),
    flags: z
        .object({
        needsSpecialHandling: z.boolean(),
        requiresInstallation: z.boolean(),
        requiresDisassembly: z.boolean(),
        hasHazardousMaterials: z.boolean(),
        requiresPermits: z.boolean(),
    })
        .strict(),
    workflow: heavyWorkflowSchema.optional(),
})
    .strict();
/** Branche canonique complète de `heavyData` après enrichissement backoffice. */
export const heavyShipmentLifecycleDataSchema = z
    .object({
    processingInfo: heavyProcessingInfoSchema,
    customRequirements: z.array(z.string()),
    documents: heavyDocumentsSchema,
    communications: z.array(heavyCommunicationSchema),
    backofficeInfo: heavyBackofficeSchema,
})
    .strict();
/**
 * Compatibilité legacy en lecture uniquement.
 *
 * Reconstruit la map canonique des 14 étapes à partir des anciens tableaux,
 * maps partielles ou débris de workflow. Les champs inconnus ne sont jamais
 * recopiés et les étapes absentes reprennent le catalogue contractuel.
 */
export function normalizeHeavyWorkflowSteps(raw) {
    const candidates = new Map();
    if (Array.isArray(raw))
        raw.forEach((value, index) => candidates.set(index + 1, value));
    else
        for (const [key, value] of Object.entries(asDomainRecord(raw) ?? {}))
            candidates.set(Number(key), value);
    const result = {};
    for (let step = 1; step <= HEAVY_WORKFLOW_STEP_COUNT; step++) {
        const candidate = asDomainRecord(candidates.get(step));
        const parsed = heavyWorkflowStepSchema.safeParse(candidate);
        const template = HEAVY_WORKFLOW_STEP_CATALOG[step - 1];
        const existing = parsed.success && parsed.data.stepNumber === step ? parsed.data : candidate;
        result[String(step)] = {
            stepNumber: step,
            name: template.name,
            description: template.description,
            status: heavyWorkflowStepStatusSchema.safeParse(existing?.status).success
                ? existing?.status
                : 'pending',
            ...(existing?.startedAt != null ? { startedAt: existing.startedAt } : {}),
            ...(existing?.completedAt != null ? { completedAt: existing.completedAt } : {}),
            ...(typeof existing?.completedBy === 'string' ? { completedBy: existing.completedBy } : {}),
            ...(typeof existing?.notes === 'string' ? { notes: existing.notes } : {}),
        };
    }
    return result;
}
export function isCanonicalHeavyWorkflowStepMap(raw) {
    if (!asDomainRecord(raw) || Object.keys(raw).length !== HEAVY_WORKFLOW_STEP_COUNT)
        return false;
    return HEAVY_WORKFLOW_STEP_CATALOG.every(({ stepNumber }) => asDomainRecord(raw)?.[String(stepNumber)] &&
        asDomainRecord(asDomainRecord(raw)?.[String(stepNumber)])?.stepNumber === stepNumber);
}
export function heavyWorkflowStepsToArray(raw) {
    const map = normalizeHeavyWorkflowSteps(raw);
    return HEAVY_WORKFLOW_STEP_CATALOG.map(({ stepNumber }) => map[String(stepNumber)]);
}
/**
 * Alias legacy conservés pour la compatibilité des façades existantes.
 * Ils délèguent tous à l'implémentation canonique ci-dessus et ne constituent
 * pas une seconde règle métier.
 */
export const normalizeHeavySteps = normalizeHeavyWorkflowSteps;
export const isCanonicalHeavyStepMap = isCanonicalHeavyWorkflowStepMap;
export const heavyStepsToArray = heavyWorkflowStepsToArray;
export function stepMapToArray(map) {
    return HEAVY_WORKFLOW_STEP_CATALOG.map(({ stepNumber }) => map[String(stepNumber)]);
}
export function deriveHeavyCurrentStep(raw) {
    let current = 1;
    for (const step of heavyWorkflowStepsToArray(raw)) {
        if (step.status === 'in_progress')
            return step.stepNumber;
        if (step.status === 'completed')
            current = step.stepNumber + 1;
    }
    return Math.min(current, HEAVY_WORKFLOW_STEP_COUNT);
}
export function deriveCurrentStep(steps) {
    return deriveHeavyCurrentStep(steps);
}
export const HEAVY_WORKFLOW_DECISION_CODES = [
    'ok', 'invalid_step', 'previous_step_incomplete', 'quote_expired',
    'quote_not_accepted', 'payment_missing', 'collection_invalid',
];
export const heavyWorkflowDecisionCodeSchema = z.enum(HEAVY_WORKFLOW_DECISION_CODES);
/**
 * Évalue l'expiration d'un devis en tolérant les timestamps Firestore legacy
 * à la lecture. Une date absente ou illisible n'est pas considérée expirée.
 */
export function isHeavyQuoteExpired(validUntil, now = new Date()) {
    if (!validUntil)
        return false;
    const date = persistedTimestampToDate(validUntil);
    return date !== null && date.getTime() < now.getTime();
}
export function expectedHeavyPaymentCents(quoteAmount) {
    const amount = Number(quoteAmount);
    return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : null;
}
export const heavyPaymentAnomalyInputSchema = z
    .object({
    expectedCents: z.number().int().nonnegative(), chargedCents: z.number().int().nonnegative(),
    quoteAccepted: z.boolean(), shipmentCancelled: z.boolean().optional(),
})
    .strict();
export const heavyPaymentAnomalySchema = z
    .object({
    reason: heavyPaymentAnomalyReasonSchema,
    amountDeltaCents: z.number().int(),
})
    .strict();
export function detectHeavyPaymentAnomaly(input) {
    if (input.shipmentCancelled)
        return {
            reason: 'shipment_cancelled',
            amountDeltaCents: input.chargedCents - input.expectedCents,
        };
    if (!input.quoteAccepted)
        return {
            reason: 'quote_rejected',
            amountDeltaCents: input.chargedCents - input.expectedCents,
        };
    if (input.chargedCents < input.expectedCents)
        return {
            reason: 'amount_underpaid',
            amountDeltaCents: input.chargedCents - input.expectedCents,
        };
    return null;
}
export const heavyCollectionValidationSchema = z
    .object({ valid: z.boolean(), errors: z.array(z.string()) })
    .strict();
export function validateHeavyCollectionConfig(raw) {
    const result = heavyCollectionConfigSchema.safeParse(raw);
    if (!result.success)
        return {
            valid: false,
            errors: result.error.issues.map((issue) => issue.path.join('.') || issue.message),
        };
    const errors = [];
    if (!result.data.scheduledDate)
        errors.push('scheduledDate');
    if (result.data.labelDeliveryMode === 'driver' && !result.data.selectedPartnerId)
        errors.push('selectedPartnerId');
    return { valid: errors.length === 0, errors };
}
export function evaluateHeavyStepTransition(input, now = new Date()) {
    if (!Number.isInteger(input.to) || input.to < 1 || input.to > HEAVY_WORKFLOW_STEP_COUNT)
        return { allowed: false, code: 'invalid_step' };
    if (input.to > input.from + 1)
        return { allowed: false, code: 'previous_step_incomplete' };
    if (input.to === 11 && isHeavyQuoteExpired(input.quoteValidUntil, now))
        return { allowed: false, code: 'quote_expired' };
    if (input.to >= 12 && !input.quoteAccepted)
        return { allowed: false, code: 'quote_not_accepted' };
    if (input.to >= 14 && !input.paid)
        return { allowed: false, code: 'payment_missing' };
    if (input.to === 14 && !validateHeavyCollectionConfig(input.collectionConfig).valid)
        return { allowed: false, code: 'collection_invalid' };
    return { allowed: true, code: 'ok' };
}
export const heavyStepTransitionInputSchema = z
    .object({
    from: z.number().int(), to: z.number().int(), steps: z.unknown(), quoteValidUntil: z.unknown().optional(),
    quoteAccepted: z.boolean().optional(), paid: z.boolean().optional(), collectionConfig: z.unknown().optional(),
})
    .strict();
export const heavyWorkflowDecisionSchema = z
    .object({ allowed: z.boolean(), code: heavyWorkflowDecisionCodeSchema })
    .strict();
